import {
  AnalyticsConfigurationKeys,
  AnalyticsConfigurations,
  ANALYTICS_NAMESPACE,
} from '@epicuro-next/constants/rule-engine/analytics-rules';
import { readHostListAnalyticsConfiguration } from '@epicuro-next/constants/rule-engine/product-list-rules';
import {
  getValueFromConfigState,
  selectConfigurationsState,
} from '@epicuro-next/platform/configuration';
import { memoize } from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';

import { getDateRangeFromHottestListConfig } from '../utils/hottest-list';

import { selectAnalyticsState } from './analytics-base.selectors';
import { selectListAnalyticsEntityFactory } from './analytics-entities.selectors';

export const selectHotListFeatureConfiguration = createSelector(
  selectConfigurationsState,
  (config) => {
    const strValue = getValueFromConfigState<
      AnalyticsConfigurationKeys.AnalyticsHottestListConfiguration,
      AnalyticsConfigurations
    >(
      config,
      ANALYTICS_NAMESPACE,
      AnalyticsConfigurationKeys.AnalyticsHottestListConfiguration,
      '',
    );

    if (!strValue) return undefined;
    return readHostListAnalyticsConfiguration(strValue);
  },
);

export const selectHotListDateRange = createSelector(
  selectHotListFeatureConfiguration,
  (config) => {
    if (!config) return undefined;
    return getDateRangeFromHottestListConfig(config);
  },
);

export const selectTopLists = createSelector(
  selectAnalyticsState,
  (state) => state.topLists,
);

export const selectHotListFeatureDataForEntityFactory = memoize(
  (listId: string) =>
    createSelector(
      selectListAnalyticsEntityFactory(listId),
      selectTopLists,
      selectHotListFeatureConfiguration,
      (entity, topLists, config) => {
        if (!config || !topLists) return undefined;

        const {
          minimumBestListRevenueInEUR,
          showAlmostHotIcons,
          almostHotListPercentage,
        } = config;
        const entityInTopLists = topLists.find(
          (topList) => topList.listId === listId,
        );
        const topListAvg =
          topLists.reduce((prev, curr) => prev + curr.totalRevenue, 0) /
          topLists.length;

        const topListRevenue = Math.max(
          topListAvg,
          minimumBestListRevenueInEUR,
        );

        const revenueOfEntity =
          entity?.report?.placed?.revenueDateConstraint?.value ??
          entityInTopLists?.totalRevenue ??
          0;

        const totalPercentage = (100 - almostHotListPercentage) / 100;

        const almostHotListDifferenceInEUR =
          topListRevenue - topListRevenue * totalPercentage;

        const almostHotListLimitRevenue = Math.max(
          topListRevenue - almostHotListDifferenceInEUR,
          0,
        );

        const hot =
          !!entityInTopLists && revenueOfEntity > minimumBestListRevenueInEUR;

        if (!showAlmostHotIcons) return { hot };

        const diffToHotIfInRange =
          revenueOfEntity > almostHotListLimitRevenue
            ? topListRevenue - revenueOfEntity
            : undefined;

        return {
          hot,
          almostHot: !hot ? diffToHotIfInRange : undefined,
        };
      },
    ),
);
